import { useEffect, useState, useCallback, useRef } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import { useToast } from '../context/ToastContext';

const API_BASE = process.env.REACT_APP_API_URL || '';

/** Redimensionne côté client à targetSize×targetSize en WebP (cover crop). */
async function resizeImage(file, targetSize = 256) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = targetSize;
      canvas.height = targetSize;
      const ctx = canvas.getContext('2d');
      const scale = Math.max(targetSize / img.width, targetSize / img.height);
      const sw = img.width * scale;
      const sh = img.height * scale;
      ctx.drawImage(img, -(sw - targetSize) / 2, -(sh - targetSize) / 2, sw, sh);
      URL.revokeObjectURL(url);
      canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Canvas vide')), 'image/webp', 0.85);
    };
    img.onerror = reject;
    img.src = url;
  });
}

const EMPTY_GROUP = { name: '', icon: '📦' };
const EMPTY_CAT   = { group_id: '', name: '', icon: '🔧', has_employment_type: false, sort_order: 0, is_active: true };

export default function Categories() {
  const toast = useToast();
  const [groups, setGroups]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [expanded, setExpanded]     = useState({});
  // Modaux
  const [groupModal, setGroupModal] = useState(null); // null | { mode:'create'|'edit', data }
  const [catModal, setCatModal]     = useState(null); // null | { mode:'create'|'edit', data }

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/categories')
      .then(r => {
        setGroups(r.data);
        // Expand tous les groupes par défaut
        const exp = {};
        r.data.forEach(g => { exp[g.id] = true; });
        setExpanded(exp);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Groupes ───────────────────────────────────────────────────────────────
  const saveGroup = async (data) => {
    try {
      if (groupModal.mode === 'create') {
        await api.post('/admin/groups', data);
        toast('Groupe créé ✓', 'success');
      } else {
        await api.put(`/admin/groups/${groupModal.data.id}`, data);
        toast('Groupe mis à jour ✓', 'success');
      }
      setGroupModal(null);
      load();
    } catch (e) { toast(e.response?.data?.message || 'Erreur', 'error'); }
  };

  const deleteGroup = async (id) => {
    if (!window.confirm('Supprimer ce groupe ? Ses catégories doivent être vides.')) return;
    try {
      await api.delete(`/admin/groups/${id}`);
      toast('Groupe supprimé', 'success');
      load();
    } catch (e) { toast(e.response?.data?.message || 'Erreur', 'error'); }
  };

  // ── Catégories ────────────────────────────────────────────────────────────
  const saveCat = async (data, imageFile) => {
    try {
      if (catModal.mode === 'create') {
        await api.post('/admin/categories', data);
        toast('Catégorie créée ✓', 'success');
      } else {
        if (imageFile) {
          const fd = new FormData();
          Object.entries(data).forEach(([k, v]) => fd.append(k, v ?? ''));
          fd.append('image', imageFile, 'category.webp');
          await api.put(`/admin/categories/${catModal.data.id}`, fd);
        } else {
          await api.put(`/admin/categories/${catModal.data.id}`, data);
        }
        toast('Catégorie mise à jour ✓', 'success');
      }
      setCatModal(null);
      load();
    } catch (e) { toast(e.response?.data?.message || 'Erreur', 'error'); }
  };

  const toggleCat = async (id) => {
    try {
      await api.put(`/admin/categories/${id}/toggle`);
      load();
    } catch (e) { toast('Erreur', 'error'); }
  };

  const deleteCat = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    try {
      await api.delete(`/admin/categories/${id}`);
      toast('Catégorie supprimée', 'success');
      load();
    } catch (e) { toast(e.response?.data?.message || 'Erreur', 'error'); }
  };

  const totalCats = groups.reduce((s, g) => s + (g.categories?.length || 0), 0);

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 className="page-title">Catégories de services</h2>
          <p className="page-subtitle">{groups.length} groupes · {totalCats} catégories</p>
        </div>
        <button className="btn btn-primary" onClick={() => setGroupModal({ mode: 'create', data: { ...EMPTY_GROUP } })}>
          + Nouveau groupe
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[1, 2, 3].map(i => <div key={i} className="skeleton" style={{ height: 80, borderRadius: 12 }} />)}
        </div>
      ) : groups.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">📂</div>
            <p className="empty-state-text">Aucun groupe de catégories</p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {groups.map(group => (
            <div key={group.id} className="card animate-fade">
              {/* En-tête du groupe */}
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', cursor: 'pointer', borderBottom: expanded[group.id] ? '1px solid var(--border)' : 'none' }}
                onClick={() => setExpanded(e => ({ ...e, [group.id]: !e[group.id] }))}
              >
                <span style={{ fontSize: 24 }}>{group.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-primary)' }}>{group.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {group.categories?.length || 0} catégorie{(group.categories?.length || 0) !== 1 ? 's' : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={e => { e.stopPropagation(); setCatModal({ mode: 'create', data: { ...EMPTY_CAT, group_id: group.id } }); }}
                  >
                    + Catégorie
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={e => { e.stopPropagation(); setGroupModal({ mode: 'edit', data: { ...group } }); }}
                  >
                    ✏️
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={e => { e.stopPropagation(); deleteGroup(group.id); }}
                  >
                    🗑
                  </button>
                  <span style={{ color: 'var(--text-muted)', fontSize: 14, marginLeft: 4 }}>
                    {expanded[group.id] ? '▲' : '▼'}
                  </span>
                </div>
              </div>

              {/* Liste des catégories */}
              {expanded[group.id] && (
                <div>
                  {(!group.categories || group.categories.length === 0) ? (
                    <div style={{ padding: '20px 24px', color: 'var(--text-muted)', fontSize: 13, textAlign: 'center' }}>
                      Aucune catégorie — cliquez sur "+ Catégorie" pour en ajouter
                    </div>
                  ) : (
                    <table className="table" style={{ marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>Icône</th>
                          <th>Nom</th>
                          <th>Type de contrat</th>
                          <th>Ordre</th>
                          <th>Statut</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.categories.map(cat => (
                          <tr key={cat.id} style={{ opacity: cat.is_active ? 1 : 0.5 }}>
                            <td>
                              {cat.image_url ? (
                                <img
                                  src={`${API_BASE}${cat.image_url}`}
                                  alt={cat.name}
                                  style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--border)' }}
                                />
                              ) : (
                                <span style={{ fontSize: 22 }}>{cat.icon}</span>
                              )}
                            </td>
                            <td style={{ fontWeight: 600 }}>{cat.name}</td>
                            <td>
                              {cat.has_employment_type
                                ? <span className="badge badge-info">Oui</span>
                                : <span className="badge" style={{ background: 'var(--surface-2)', color: 'var(--text-muted)' }}>Non</span>}
                            </td>
                            <td style={{ color: 'var(--text-muted)' }}>{cat.sort_order}</td>
                            <td>
                              <button
                                className={`badge ${cat.is_active ? 'badge-success' : 'badge-danger'}`}
                                style={{ cursor: 'pointer', border: 'none', background: cat.is_active ? 'var(--success-light)' : 'var(--danger-light)', color: cat.is_active ? 'var(--success)' : 'var(--danger)' }}
                                onClick={() => toggleCat(cat.id)}
                                title="Cliquer pour activer/désactiver"
                              >
                                {cat.is_active ? '● Actif' : '● Inactif'}
                              </button>
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => setCatModal({ mode: 'edit', data: { ...cat } })}
                                >
                                  ✏️ Modifier
                                </button>
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => deleteCat(cat.id)}
                                >
                                  🗑
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Modal groupe ── */}
      <Modal
        open={!!groupModal}
        onClose={() => setGroupModal(null)}
        title={groupModal?.mode === 'create' ? 'Nouveau groupe' : 'Modifier le groupe'}
      >
        {groupModal && (
          <GroupForm
            initial={groupModal.data}
            onSave={saveGroup}
            onCancel={() => setGroupModal(null)}
          />
        )}
      </Modal>

      {/* ── Modal catégorie ── */}
      <Modal
        open={!!catModal}
        onClose={() => setCatModal(null)}
        title={catModal?.mode === 'create' ? 'Nouvelle catégorie' : 'Modifier la catégorie'}
      >
        {catModal && (
          <CategoryForm
            initial={catModal.data}
            groups={groups}
            onSave={saveCat}
            onCancel={() => setCatModal(null)}
          />
        )}
      </Modal>
    </div>
  );
}

// ── Formulaire groupe ─────────────────────────────────────────────────────────
function GroupForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({ ...initial });
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="form-group">
        <label className="form-label">Icône (emoji)</label>
        <input className="form-input" value={form.icon} onChange={set('icon')} placeholder="📦" style={{ maxWidth: 80, fontSize: 22, textAlign: 'center' }} />
      </div>
      <div className="form-group">
        <label className="form-label">Nom du groupe *</label>
        <input className="form-input" value={form.name} onChange={set('name')} placeholder="Ex : Services à domicile" />
      </div>
      <div className="form-group">
        <label className="form-label">Ordre d'affichage</label>
        <input className="form-input" type="number" value={form.sort_order || 0} onChange={set('sort_order')} style={{ maxWidth: 120 }} />
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
        <button className="btn btn-ghost" onClick={onCancel}>Annuler</button>
        <button className="btn btn-primary" onClick={() => onSave(form)} disabled={!form.name?.trim()}>
          Enregistrer
        </button>
      </div>
    </div>
  );
}

// ── Formulaire catégorie ──────────────────────────────────────────────────────
function CategoryForm({ initial, groups, onSave, onCancel }) {
  const [form, setForm]           = useState({ ...initial });
  const [imageFile, setImageFile] = useState(null);   // File WebP redimensionné
  const [preview, setPreview]     = useState(null);   // data URL pour aperçu
  const [resizing, setResizing]   = useState(false);
  const fileRef                   = useRef();
  const isEdit                    = !!initial.id;

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setResizing(true);
    try {
      const blob    = await resizeImage(file, 256);
      const resized = new File([blob], 'category.webp', { type: 'image/webp' });
      setImageFile(resized);
      setPreview(URL.createObjectURL(blob));
    } catch {
      alert('Impossible de traiter cette image.');
    } finally {
      setResizing(false);
      e.target.value = '';
    }
  };

  const removeImage = () => { setImageFile(null); setPreview(null); };

  const currentSrc = preview || (form.image_url ? `${API_BASE}${form.image_url}` : null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Avatar : emoji + import image (mode édition uniquement) ── */}
      <div className="form-group">
        <label className="form-label">Avatar</label>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

          {/* Vignette 80×80 */}
          <div style={{
            width: 80, height: 80, borderRadius: 12, flexShrink: 0, overflow: 'hidden',
            border: '2px dashed var(--border)', background: 'var(--surface-2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
          }}>
            {currentSrc ? (
              <>
                <img src={currentSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button
                  type="button"
                  onClick={removeImage}
                  title="Retirer l'image"
                  style={{
                    position: 'absolute', top: 3, right: 3, width: 18, height: 18,
                    borderRadius: '50%', border: 'none', cursor: 'pointer',
                    background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 10, lineHeight: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >✕</button>
              </>
            ) : (
              <span style={{ fontSize: 36 }}>{form.icon || '🔧'}</span>
            )}
          </div>

          {/* Contrôles à droite */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Emoji :</label>
              <input
                className="form-input"
                value={form.icon}
                onChange={set('icon')}
                placeholder="🔧"
                style={{ width: 64, fontSize: 20, textAlign: 'center', padding: '6px 4px' }}
              />
            </div>

            {isEdit && (
              <>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => fileRef.current?.click()}
                  disabled={resizing}
                  style={{ alignSelf: 'flex-start' }}
                >
                  {resizing ? '⏳ Traitement…' : currentSrc ? '🔄 Changer l\'image' : '📷 Importer une image'}
                </button>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  JPG / PNG / WEBP · Redimensionné à 256 × 256 px
                </span>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handleImageChange} />
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Nom ── */}
      <div className="form-group">
        <label className="form-label">Nom *</label>
        <input className="form-input" value={form.name} onChange={set('name')} placeholder="Ex : Plombier" />
      </div>

      {/* ── Groupe ── */}
      <div className="form-group">
        <label className="form-label">Groupe *</label>
        <select className="form-input form-select" value={form.group_id} onChange={set('group_id')}>
          <option value="">— Choisir —</option>
          {groups.map(g => <option key={g.id} value={g.id}>{g.icon} {g.name}</option>)}
        </select>
      </div>

      {/* ── Ordre + Statut ── */}
      <div style={{ display: 'flex', gap: 12 }}>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Ordre</label>
          <input className="form-input" type="number" value={form.sort_order || 0} onChange={set('sort_order')} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Statut</label>
          <select className="form-input form-select" value={form.is_active ? '1' : '0'} onChange={e => setForm(f => ({ ...f, is_active: e.target.value === '1' }))}>
            <option value="1">Actif</option>
            <option value="0">Inactif</option>
          </select>
        </div>
      </div>

      {/* ── Type de contrat ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--surface-2)', borderRadius: 8 }}>
        <input type="checkbox" id="emp_type" checked={!!form.has_employment_type} onChange={set('has_employment_type')} style={{ width: 16, height: 16 }} />
        <label htmlFor="emp_type" style={{ fontSize: 14, cursor: 'pointer' }}>
          Proposer un <strong>type de contrat</strong> (temps plein, partiel, journalier…)
        </label>
      </div>

      {/* ── Boutons ── */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
        <button className="btn btn-ghost" onClick={onCancel}>Annuler</button>
        <button
          className="btn btn-primary"
          onClick={() => onSave(form, imageFile)}
          disabled={!form.name?.trim() || !form.group_id || resizing}
        >
          Enregistrer
        </button>
      </div>
    </div>
  );
}
